import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useMember } from '../context/MemberContext';
import { useAppInfo } from '../context/AppContext';
import { useSite } from '../context/SiteContext';
import './AuthPage.css';

interface MemberLoginResponse {
  data: {
    apiKey: string;
    personId: number;
    firstName: string;
    lastName: string;
    email: string;
    siteId?: number;
    logoUrl?: string;
    appName?: string;
    qrCodeUrl?: string;
    isActive?: boolean;
  };
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { setMember } = useMember();
  const { logoUrl: appLogoUrl } = useAppInfo();
  const { site } = useSite();
  const logoUrl = site?.logoUrl ?? appLogoUrl;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await apiClient.post<MemberLoginResponse>(
        '/Authentication/PwaMemberLogin',
        { email, password }
      );
      const { apiKey, personId, firstName, lastName, email: memberEmail, siteId, logoUrl, appName, qrCodeUrl, isActive } = res.data.data;
      setMember({ personId, firstName, lastName, email: memberEmail, siteId, logoUrl, appName, qrCodeUrl, isActive }, apiKey);
      navigate('/');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Invalid email or password.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      {logoUrl && (
        <img src={logoUrl} alt="Logo" className="auth-page-logo" />
      )}
      <div className="auth-card">
        <h1 className="auth-title">Sign In</h1>
        <form onSubmit={handleSubmit} className="auth-form">
          <label className="auth-label">
            Email
            <input
              className="auth-input"
              type="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </label>
          <label className="auth-label">
            Password
            <div className="auth-input-wrap">
              <input
                className="auth-input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="auth-eye"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={20} height={20}>
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
          </label>
          {error && <p className="auth-error">{error}</p>}
          <button className="auth-btn" type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
        </form>
        <p className="auth-footer">
          <Link to="/forgotpassword">Forgot password?</Link>
        </p>
        <p className="auth-footer">
          New member? <Link to="/signup">Create account</Link>
        </p>
      </div>
    </div>
  );
}
